/**
 * The one place this app talks to the backend.
 *
 * Everything is same-origin and relative — Caddy serves these files and proxies
 * /v1 to the api, and locally vite.config.ts's dev-server proxy does the same
 * job. There is therefore no base URL, no CORS, and no api-side CORS plugin to
 * add (DEPLOY-DEV.md §7 item 2).
 *
 * NOTHING HERE INVENTS DATA. No fallback bodies, no canned responses: a failure
 * becomes an ApiFailure carrying the backend's own envelope, and screens render
 * that verbatim. If a flow is not wired on the environment behind this client,
 * the screen must show it.
 */

import { isAdminPath } from '../admin/routes.js';

/** `{ error, message, retry_after? }` — src/errors/app-error.ts. */
export interface ErrorEnvelope {
  error: string;
  message: string;
  retry_after?: number;
  [extra: string]: unknown;
}

export class ApiFailure extends Error {
  readonly status: number;
  /** A member of the closed union in src/errors/codes.ts, or 'TRANSPORT'. */
  readonly code: string;
  readonly retryAfter: number | null;
  /** Whatever the response actually was, for the detail disclosure. */
  readonly body: unknown;

  constructor(status: number, code: string, message: string, retryAfter: number | null, body: unknown) {
    super(message);
    this.name = 'ApiFailure';
    this.status = status;
    this.code = code;
    this.retryAfter = retryAfter;
    this.body = body;
  }
}

export interface TokenPair {
  access_token: string;
  expires_in: number;
  refresh_token: string;
}

// ---------------------------------------------------------------------------
// Session
// ---------------------------------------------------------------------------

/**
 * sessionStorage, NOT localStorage, and the access token is never persisted at
 * all. The environment behind this client holds real consented faces
 * (DEPLOY-DEV.md §8), so a closed tab must not leave a resumable session on a
 * demo laptop. A reload keeps you signed in; quitting the browser does not.
 */
/**
 * WHICH surface this page is: the consumer app, or the control room at /admin.
 * Both share one origin and therefore one sessionStorage, so each keeps its
 * refresh token under its own key — signing out of the panel never signs the
 * app out, and a consumer session lying in the tab never silently becomes a
 * control-room session. Read once, at load: in a hash-routed app, /admin and /
 * are different page loads.
 */
export type SessionRealm = 'app' | 'admin';

const REALM: SessionRealm = (() => {
  try {
    return isAdminPath(window.location.pathname) ? 'admin' : 'app';
  } catch {
    // No `location` (tests stub a two-property window) — the consumer app.
    return 'app';
  }
})();

export function sessionRealm(): SessionRealm {
  return REALM;
}

const REFRESH_STORAGE_KEY =
  REALM === 'admin' ? 'imageshield.admin.refresh_token' : 'imageshield.refresh_token';

function readStoredRefresh(): string | null {
  try {
    return window.sessionStorage.getItem(REFRESH_STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeStoredRefresh(token: string | null): void {
  try {
    if (token === null) window.sessionStorage.removeItem(REFRESH_STORAGE_KEY);
    else window.sessionStorage.setItem(REFRESH_STORAGE_KEY, token);
  } catch {
    // Private-mode or blocked storage: the session still works for this page.
  }
}

let accessToken: string | null = null;
let refreshToken: string | null = readStoredRefresh();

/**
 * WHY the session ended, when it ended without the user asking it to. The
 * first shipped client cleared silently: a deleted account's browser bounced
 * to the signed-out screen with no explanation, over and over, and the reason
 * (403 "account is not active") existed only in the api's logs. In memory
 * only — it describes this page's session, and a reload that still has a
 * refresh token will find out for itself.
 */
export interface SignoutReason {
  code: string;
  message: string;
}

let signoutReason: SignoutReason | null = null;

export function readSignoutReason(): SignoutReason | null {
  return signoutReason;
}

const authListeners = new Set<() => void>();

function announceAuthChange(): void {
  for (const listener of authListeners) listener();
}

export function subscribeAuth(listener: () => void): () => void {
  authListeners.add(listener);
  return () => authListeners.delete(listener);
}

/** A refresh token is what "signed in" means: the access token may be expired. */
export function isSignedIn(): boolean {
  return refreshToken !== null;
}

export function setTokens(pair: TokenPair): void {
  accessToken = pair.access_token;
  refreshToken = pair.refresh_token;
  writeStoredRefresh(pair.refresh_token);
  signoutReason = null;
  announceAuthChange();
}

/** No reason = the user chose this (sign out, account deletion): no notice. */
export function clearSession(reason?: SignoutReason): void {
  accessToken = null;
  refreshToken = null;
  writeStoredRefresh(null);
  signoutReason = reason ?? null;
  announceAuthChange();
}

// ---------------------------------------------------------------------------
// Refresh, single-flight
// ---------------------------------------------------------------------------

/**
 * ONE refresh at a time, shared by every caller that saw a 401.
 *
 * Parallel reads are the normal case here (the home screen fires /v1/home and
 * /v1/me/score together). Two concurrent refreshes would rotate the refresh
 * token twice, and the backend reads the second use of a rotated token as
 * replay: it revokes the whole family and answers TOKEN_REUSED. So refreshing
 * twice does not merely waste a call, it signs the user out.
 */
let refreshInFlight: Promise<boolean> | null = null;

function refreshAccess(): Promise<boolean> {
  refreshInFlight ??= performRefresh().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

async function performRefresh(): Promise<boolean> {
  const token = refreshToken;
  if (token === null) return false;
  let response: Response;
  try {
    response = await sendOnce('POST', '/v1/auth/refresh', {
      body: { refresh_token: token },
      anonymous: true,
    });
  } catch {
    // Network failure: the session may still be good, so it is NOT cleared.
    return false;
  }
  if (!response.ok) {
    // ONLY an auth refusal ends the session: 401 (expired, rotated away,
    // TOKEN_REUSED family revocation) or 403 (the account itself is refused —
    // suspended, mid-deletion). The first shipped client cleared on ANY
    // non-ok, so a 502 from Caddy during a deploy signed everyone out. A
    // transient failure leaves the tokens alone; the next request simply
    // tries again.
    if (response.status === HTTP_UNAUTHORISED || response.status === HTTP_FORBIDDEN) {
      const envelope = envelopeOf(parseJson(await response.text()));
      clearSession(
        envelope === null
          ? { code: 'UNAUTHORISED', message: 'your session ended — sign in again' }
          : { code: envelope.error, message: envelope.message },
      );
    }
    return false;
  }
  setTokens((await response.json()) as TokenPair);
  return true;
}

// ---------------------------------------------------------------------------
// Requests
// ---------------------------------------------------------------------------

export type QueryValue = string | number | boolean | undefined;

export interface RequestOptions {
  body?: unknown;
  query?: Record<string, QueryValue>;
  /**
   * Sent as Idempotency-Key. Required by eight routes; REFUSED by
   * POST /v1/enrolment/sessions, which 400s if the header is present at all
   * (creating a session burns a liveness attempt).
   */
  idempotencyKey?: string;
  /** Skip Authorization and the 401-refresh dance (login and refresh itself). */
  anonymous?: boolean;
  /**
   * Retry a 409 that carries retry_after, reusing the SAME idempotency key.
   * That 409 means a duplicate is in flight, not that the request was wrong.
   */
  retryConflict?: boolean;
  /** Overrides `Accept: application/json` — only the crop relay needs this. */
  accept?: string;
  /** `cache: 'no-store'`. Set for anything the browser must not keep. */
  noStore?: boolean;
}

function buildUrl(path: string, query?: Record<string, QueryValue>): string {
  if (query === undefined) return path;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined) params.set(key, String(value));
  }
  const encoded = params.toString();
  return encoded.length === 0 ? path : `${path}?${encoded}`;
}

async function sendOnce(method: string, url: string, options: RequestOptions): Promise<Response> {
  const headers: Record<string, string> = { Accept: options.accept ?? 'application/json' };
  if (options.body !== undefined) headers['Content-Type'] = 'application/json';
  if (options.idempotencyKey !== undefined) headers['Idempotency-Key'] = options.idempotencyKey;
  if (options.anonymous !== true && accessToken !== null) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }
  return fetch(url, {
    method,
    headers,
    credentials: 'same-origin',
    // Opt out of the HTTP cache entirely for responses that must not be kept.
    // The api already sends `Cache-Control: no-store, private`; this is the
    // request-side half, so there is no cache entry to begin with.
    ...(options.noStore === true ? { cache: 'no-store' as RequestCache } : {}),
    ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
  });
}

function parseJson(text: string): unknown {
  try {
    return JSON.parse(text) as unknown;
  } catch {
    // An HTML error page from Caddy, or a proxy timeout. Keep it as-is: the
    // detail disclosure showing raw text beats pretending it was JSON.
    return text;
  }
}

function envelopeOf(body: unknown): ErrorEnvelope | null {
  if (typeof body !== 'object' || body === null) return null;
  const candidate = body as Record<string, unknown>;
  if (typeof candidate['error'] !== 'string' || typeof candidate['message'] !== 'string') return null;
  return candidate as unknown as ErrorEnvelope;
}

function failureOf(status: number, body: unknown): ApiFailure {
  const envelope = envelopeOf(body);
  if (envelope === null) {
    return new ApiFailure(
      status,
      'NON_ENVELOPE_RESPONSE',
      `HTTP ${String(status)} with no error envelope`,
      null,
      body,
    );
  }
  return new ApiFailure(
    status,
    envelope.error,
    envelope.message,
    typeof envelope.retry_after === 'number' ? envelope.retry_after : null,
    body,
  );
}

const HTTP_UNAUTHORISED = 401;
const HTTP_FORBIDDEN = 403;
const HTTP_CONFLICT = 409;
const HTTP_NO_CONTENT = 204;
/** A duplicate in flight clears in well under a second; three tries is plenty. */
const CONFLICT_RETRY_LIMIT = 3;

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

/**
 * `T` is what the route returns on success. Callers declare it; nothing here
 * validates the shape, because a mismatch must surface as visibly wrong data on
 * screen rather than be silently coerced or defaulted.
 */
export async function apiRequest<T>(
  method: string,
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const url = buildUrl(path, options.query);

  /**
   * REFRESH BEFORE SENDING, not after being refused.
   *
   * A reload loses the in-memory access token and keeps the refresh token in
   * sessionStorage, so `isSignedIn()` is true with nothing to put in the
   * Authorization header. Sending anyway produces a 401 that was certain before
   * the request left the browser: the reactive path below then refreshes and
   * retries, costing two round trips and writing a 401 into the api's logs for
   * every request on every page load. The home screen alone fires three.
   *
   * Single-flight, so parallel first-paint requests share one refresh rather
   * than rotating the token three times — which the backend would read as
   * replay and answer with TOKEN_REUSED, revoking the family.
   *
   * The 401 handler below stays, and is now what it was meant to be: the path
   * for a token that expires DURING a session, not the normal case.
   */
  if (options.anonymous !== true && accessToken === null && isSignedIn()) {
    await refreshAccess();
  }

  for (let attempt = 0; ; attempt += 1) {
    let response: Response;
    try {
      response = await sendOnce(method, url, options);
    } catch (error) {
      throw new ApiFailure(
        0,
        'TRANSPORT',
        error instanceof Error ? error.message : 'the request never reached the api',
        null,
        null,
      );
    }

    if (response.status === HTTP_UNAUTHORISED && options.anonymous !== true && isSignedIn()) {
      if (await refreshAccess()) {
        response = await sendOnce(method, url, options);
      }
    }

    if (response.status === HTTP_NO_CONTENT) return undefined as T;

    const body = parseJson(await response.text());

    if (response.ok) return body as T;

    const failure = failureOf(response.status, body);

    // Only a 409 that told us WHEN to come back is a concurrent duplicate.
    // A CONFLICT from a real invariant (ALREADY_IN_HOUSEHOLD, DOB_ALREADY_SET)
    // carries no retry_after and must surface immediately.
    const retryable =
      options.retryConflict === true &&
      response.status === HTTP_CONFLICT &&
      failure.retryAfter !== null &&
      attempt < CONFLICT_RETRY_LIMIT;
    if (!retryable) throw failure;

    await delay(failure.retryAfter * 1000);
  }
}

/**
 * THE ONE NON-JSON READ: the subject's blurred face crop.
 *
 * `GET /v1/hits/{id}/preview` is session-authed, and an `<img src>` cannot carry
 * an Authorization header — so the bytes are fetched here, handed to the caller
 * as a Blob, and turned into an object URL at the point of use. Whoever calls
 * this OWNS revoking that URL: an object URL outlives the component that made
 * it, and this one points at a picture of somebody's abuse.
 *
 * NOTHING CACHES IT. `cache: 'no-store'` on the way out, `Cache-Control:
 * no-store, private` on the way back, no service worker in this app, and the
 * blob is never written to storage of any kind. The bytes exist for one render.
 *
 * Error bodies are still JSON, so the four preview outcomes
 * (HIT_NOT_FOUND · PREVIEW_UNAVAILABLE · RATE_LIMITED · PREVIEW_UNAVAILABLE
 * again, from a 502) arrive as an ordinary ApiFailure and the ask-card can tell
 * them apart. Two of the four are not errors at all.
 *
 * Shares the 401-refresh dance with apiRequest deliberately: a token that
 * expires while somebody is looking at their own hit must refresh and replay,
 * not surface as a broken image.
 */
export async function apiBlob(
  path: string,
  query?: Record<string, QueryValue>,
): Promise<Blob> {
  const url = buildUrl(path, query);
  const options: RequestOptions = { accept: 'image/jpeg', noStore: true };

  // Same reasoning as apiRequest: a reload has the refresh token and no access
  // token, so sending first would guarantee a 401.
  if (accessToken === null && isSignedIn()) await refreshAccess();

  let response: Response;
  try {
    response = await sendOnce('GET', url, options);
  } catch (error) {
    throw new ApiFailure(
      0,
      'TRANSPORT',
      error instanceof Error ? error.message : 'the request never reached the api',
      null,
      null,
    );
  }

  if (response.status === HTTP_UNAUTHORISED && isSignedIn()) {
    if (await refreshAccess()) response = await sendOnce('GET', url, options);
  }

  if (!response.ok) throw failureOf(response.status, parseJson(await response.text()));
  return response.blob();
}

/**
 * The direct-to-S3 PUT. Cross-origin by definition, and therefore needs
 * `CORS on imageshield-dev-uploads allowing PUT from https://dev.imageshield.com`
 * (DEPLOY-DEV.md §7 item 2) before it can work on the deployed environment.
 *
 * NO Authorization header — the signature is the authorisation. Content-Type
 * MUST equal the content_type sent when minting: src/media/photos.ts presigns
 * with ContentType set, so it is a signed header and a mismatch is a 403
 * SignatureDoesNotMatch rather than a helpful error.
 */
export async function putToPresignedUrl(
  url: string,
  file: Blob,
  contentType: string,
): Promise<void> {
  let response: Response;
  try {
    response = await fetch(url, {
      method: 'PUT',
      headers: { 'Content-Type': contentType },
      body: file,
      mode: 'cors',
    });
  } catch (error) {
    throw new ApiFailure(
      0,
      'S3_TRANSPORT',
      `${error instanceof Error ? error.message : 'upload failed'} — the uploads bucket may not allow PUT from this origin (DEPLOY-DEV.md §7.2)`,
      null,
      null,
    );
  }
  if (!response.ok) {
    throw new ApiFailure(response.status, 'S3_REJECTED', await response.text(), null, null);
  }
}

/** One key per user intent, reused across every retry of that intent. */
export function newIdempotencyKey(): string {
  return crypto.randomUUID();
}
